import React, { useState, useRef, useEffect } from "react";
import {
  Camera,
  Edit3,
  Save,
  X,
  Settings,
  BarChart3,
  User,
  Loader2,
} from "lucide-react";
import { useAuthStore } from "@/context";
import { useAuth } from "@/composables/auth";

export function UserProfile() {
  const { currentUser, profilePicture } = useAuthStore();
  const {
    upateProfilePicture,
    savingPicture,
    updating,
    fetchProfilePicture,
    updateProfileInfos,
  } = useAuth();
  const [activeTab, setActiveTab] = useState("personal-info");
  const [isEditMode, setIsEditMode] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState(
    "✅ Vos informations ont été mises à jour avec succès !"
  );
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Form data state
  const [formData, setFormData] = useState({
    firstName: currentUser?.prenom ?? "",
    lastName: currentUser?.nom ?? "",
    email: currentUser?.email ?? "",
    phone: currentUser?.phone ?? "",
    city: currentUser?.adresse?.ville ?? "",
    company: "JourGui SARL",
    address: currentUser?.adresse?.adresseComplete ?? "",
    type: currentUser?.type ?? "",
  });

  const [originalData, setOriginalData] = useState({});

  // Security form state
  const [securityData, setSecurityData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
    language: "fr",
    emailNotifications: true,
    twoFactorAuth: true,
  });

  const stats = [
    { number: "127", label: "Commandes traitées" },
    { number: "98.5%", label: "Taux de satisfaction" },
    { number: "23", label: "Campagnes actives" },
    { number: "45 min", label: "Temps moyen de réponse" },
  ];

  useEffect(() => {
    fetchProfilePicture();
  }, []);

  const handleInputChange = (e: any) => {
    const { name, value, type, checked } = e.target;

    if (activeTab === "personal-info") {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    } else if (activeTab === "account-settings") {
      setSecurityData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }
  };

  const handleEditClick = () => {
    setOriginalData({ ...formData });
    setIsEditMode(true);
  };

  const handleCancelClick = () => {
    // setFormData(originalData);
    setIsEditMode(false);
  };

  const handleSaveClick = async (e: any) => {
    e.preventDefault();
    await updateProfileInfos({
      ...currentUser,
      nom: formData.lastName,
      prenom: formData.firstName,
      phone: formData.phone,
    });
    setIsEditMode(false);
    showSuccessMessage(
      "✅ Vos informations ont été mises à jour avec succès !"
    );
  };

  const handleSecuritySubmit = (e: any) => {
    e.preventDefault();

    if (
      securityData.newPassword &&
      securityData.newPassword !== securityData.confirmPassword
    ) {
      alert("Les mots de passe ne correspondent pas.");
      return;
    }

    // Simulate save
    setTimeout(() => {
      showSuccessMessage("🔒 Paramètres de sécurité mis à jour avec succès !");
      setSecurityData((prev) => ({
        ...prev,
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      }));
    }, 500);
  };

  const showSuccessMessage = (message: string) => {
    setSuccessMessage(message);
    setShowSuccess(true);
    setTimeout(() => {
      setShowSuccess(false);
    }, 3000);
  };

  const handleAvatarChange = async (e: any) => {
    const file = e.target.files[0];
    if (file) {
      const form = new FormData();
      form.append("fichier", file);
      await upateProfilePicture(form);
    }
  };

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <main className="flex-1 p-8 max-w-6xl">
      <div className="mb-8">
        <h1 className="text-3xl font-semibold text-gray-800 mb-2">
          Mon Profil
        </h1>
        <p className="text-gray-600">
          Gérez vos informations personnelles et préférences de compte
        </p>
      </div>

      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        {/* Profile Header */}
        <div className="bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 px-8 py-12 text-center text-white relative">
          <div className="relative inline-block mb-6">
            <img
              src={profilePicture}
              alt="Photo de profil de Babacar"
              className="w-32 h-32 rounded-full border-4 border-white object-cover"
            />
            <button
              onClick={handleAvatarClick}
              disabled={savingPicture}
              className={`absolute bottom-2 right-2 w-10 h-10 rounded-full flex items-center justify-center transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-blue-300 ${
                savingPicture
                  ? "bg-blue-400 cursor-not-allowed"
                  : "bg-blue-600 hover:bg-blue-700 hover:scale-110"
              }`}
              aria-label="Changer la photo de profil"
            >
              {savingPicture ? (
                <Loader2 size={16} className="text-white animate-spin" />
              ) : (
                <Camera size={16} className="text-white" />
              )}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleAvatarChange}
              className="hidden"
              aria-label="Sélectionner une nouvelle photo de profil"
            />
          </div>
          <h2 className="text-2xl font-semibold mb-2">
            {formData.firstName} {formData.lastName}
          </h2>
          <p className="text-lg opacity-90">{currentUser?.type ?? ""}</p>
        </div>

        {/* Profile Body */}
        <div className="p-8">
          {/* Success Message */}
          {showSuccess && (
            <div
              className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg mb-6"
              role="alert"
            >
              {successMessage}
            </div>
          )}

          {/* Tabs */}
          <div className="flex border-b-2 border-gray-200 mb-8" role="tablist">
            {[
              {
                id: "personal-info",
                label: "Informations personnelles",
                icon: User,
              },
              {
                id: "account-settings",
                label: "Paramètres du compte",
                icon: Settings,
              },
              // { id: "statistics", label: "Statistiques", icon: BarChart3 },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-3 font-medium transition-all duration-200 border-b-2 ${
                  activeTab === tab.id
                    ? "text-blue-600 border-blue-600"
                    : "text-gray-600 border-transparent hover:text-gray-800"
                }`}
                role="tab"
                aria-selected={activeTab === tab.id}
              >
                <tab.icon size={18} />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Personal Info Tab */}
          {activeTab === "personal-info" && (
            <div role="tabpanel">
              <div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  <div>
                    <label
                      htmlFor="firstName"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Prénom *
                    </label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      disabled={!isEditMode}
                      required
                      className={`w-full px-4 py-3 border-2 rounded-lg transition-all duration-200 ${
                        isEditMode
                          ? "border-blue-300 bg-blue-50 focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                          : "border-gray-200 bg-gray-50 text-gray-600"
                      } focus:outline-none`}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="lastName"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Nom *
                    </label>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      disabled={!isEditMode}
                      required
                      className={`w-full px-4 py-3 border-2 rounded-lg transition-all duration-200 ${
                        isEditMode
                          ? "border-blue-300 bg-blue-50 focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                          : "border-gray-200 bg-gray-50 text-gray-600"
                      } focus:outline-none`}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      disabled
                      className="w-full px-4 py-3 border-2 border-gray-200 bg-gray-50 text-gray-600 rounded-lg"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="phone"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Téléphone
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      disabled={!isEditMode}
                      className={`w-full px-4 py-3 border-2 rounded-lg transition-all duration-200 ${
                        isEditMode
                          ? "border-blue-300 bg-blue-50 focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                          : "border-gray-200 bg-gray-50 text-gray-600"
                      } focus:outline-none`}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="city"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Ville
                    </label>
                    <input
                      type="text"
                      id="city"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      disabled={!isEditMode}
                      className={`w-full px-4 py-3 border-2 rounded-lg transition-all duration-200 ${
                        isEditMode
                          ? "border-blue-300 bg-blue-50 focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                          : "border-gray-200 bg-gray-50 text-gray-600"
                      } focus:outline-none`}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="company"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Entreprise
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      disabled={!isEditMode}
                      className={`w-full px-4 py-3 border-2 rounded-lg transition-all duration-200 ${
                        isEditMode
                          ? "border-blue-300 bg-blue-50 focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                          : "border-gray-200 bg-gray-50 text-gray-600"
                      } focus:outline-none`}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label
                      htmlFor="address"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Adresse complète
                    </label>
                    <input
                      type="text"
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      disabled={!isEditMode}
                      className={`w-full px-4 py-3 border-2 rounded-lg transition-all duration-200 ${
                        isEditMode
                          ? "border-blue-300 bg-blue-50 focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                          : "border-gray-200 bg-gray-50 text-gray-600"
                      } focus:outline-none`}
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-3">
                  {isEditMode ? (
                    <>
                      <button
                        type="button"
                        onClick={handleCancelClick}
                        className="flex items-center gap-2 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-gray-200"
                      >
                        <X size={18} />
                        Annuler
                      </button>
                      <button
                        type="button"
                        onClick={handleSaveClick}
                        disabled={updating}
                        className={`flex items-center gap-2 px-6 py-3 text-white rounded-lg transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-blue-300 ${
                          updating
                            ? "bg-blue-400 cursor-not-allowed"
                            : "bg-blue-600 hover:bg-blue-700 hover:shadow-lg transform hover:-translate-y-0.5"
                        }`}
                      >
                        {updating ? (
                          <Loader2 size={18} className="animate-spin" />
                        ) : (
                          <Save size={18} />
                        )}
                        {updating ? "Enregistrement..." : "Enregistrer"}
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      onClick={handleEditClick}
                      className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-blue-300"
                    >
                      <Edit3 size={18} />
                      Modifier
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Account Settings Tab */}
          {activeTab === "account-settings" && (
            <div role="tabpanel">
              <form onSubmit={handleSecuritySubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  <div>
                    <label
                      htmlFor="currentPassword"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Mot de passe actuel
                    </label>
                    <input
                      type="password"
                      id="currentPassword"
                      name="currentPassword"
                      value={securityData.currentPassword}
                      onChange={handleInputChange}
                      placeholder="Votre mot de passe actuel"
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:ring-4 focus:ring-blue-100 focus:outline-none transition-all duration-200"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="newPassword"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Nouveau mot de passe
                    </label>
                    <input
                      type="password"
                      id="newPassword"
                      name="newPassword"
                      value={securityData.newPassword}
                      onChange={handleInputChange}
                      placeholder="Nouveau mot de passe"
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:ring-4 focus:ring-blue-100 focus:outline-none transition-all duration-200"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="confirmPassword"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Confirmer le mot de passe
                    </label>
                    <input
                      type="password"
                      id="confirmPassword"
                      name="confirmPassword"
                      value={securityData.confirmPassword}
                      onChange={handleInputChange}
                      placeholder="Confirmer le mot de passe"
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:ring-4 focus:ring-blue-100 focus:outline-none transition-all duration-200"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="language"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Langue préférée
                    </label>
                    <select
                      id="language"
                      name="language"
                      value={securityData.language}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:ring-4 focus:ring-blue-100 focus:outline-none transition-all duration-200"
                    >
                      <option value="fr">Français</option>
                      <option value="en">English</option>
                      <option value="ar">العربية</option>
                    </select>
                  </div>
                  <div className="md:col-span-2 space-y-4">
                    <label className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        name="emailNotifications"
                        checked={securityData.emailNotifications}
                        onChange={handleInputChange}
                        className="w-5 h-5 text-blue-600 border-2 border-gray-300 rounded focus:ring-4 focus:ring-blue-100"
                      />
                      <span className="text-gray-700 font-medium">
                        Recevoir les notifications par email
                      </span>
                    </label>
                    <label className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        name="twoFactorAuth"
                        checked={securityData.twoFactorAuth}
                        onChange={handleInputChange}
                        className="w-5 h-5 text-blue-600 border-2 border-gray-300 rounded focus:ring-4 focus:ring-blue-100"
                      />
                      <span className="text-gray-700 font-medium">
                        Activer l'authentification à deux facteurs
                      </span>
                    </label>
                  </div>
                </div>

                <div className="flex justify-end">
                  <button
                    type="submit"
                    className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-blue-300"
                  >
                    <Settings size={18} />
                    Mettre à jour la sécurité
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Statistics Tab */}
          {activeTab === "statistics" && (
            <div role="tabpanel">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                {stats.map((stat, index) => (
                  <div
                    key={index}
                    className="bg-gray-50 p-6 rounded-xl text-center"
                  >
                    <div className="text-3xl font-bold text-blue-600 mb-2">
                      {stat.number}
                    </div>
                    <div className="text-gray-600 text-sm font-medium">
                      {stat.label}
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-gray-50 p-8 rounded-xl text-center">
                <h3 className="text-xl font-semibold text-gray-800 mb-3">
                  Membre depuis
                </h3>
                <p className="text-lg font-medium text-gray-600">Mars 2023</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
